import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { getMyProfile, updateMyProfile } from '../../lib/api';
import { toast } from 'sonner';

const chip = (text) => (
  <Badge variant="outline" className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
    {text}
  </Badge>
);

export default function AgentProfile() {
  const { user, token, updateProfile: updateAuthProfile } = useAuth();
  const location = useLocation();
  const mode = (() => {
    if (location.pathname.includes('/agent/profile/bank')) return 'bank';
    if (location.pathname.includes('/agent/profile/support')) return 'support';
    return 'profile';
  })();
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (!user) return;
    setProfile({
      full_name: user.full_name || user.name || '',
      email: user.email || '',
      phone: user.phone || '',
    });
  }, [user]);

  useEffect(() => {
    const hydrateProfile = async () => {
      if (!token) return;
      try {
        const resp = await getMyProfile(token);
        const current = resp?.data;
        if (current) {
          setProfile({
            full_name: current.full_name || current.name || '',
            email: current.email || '',
            phone: current.phone || '',
          });
        }
      } catch {
        // Keep fallback from auth context when API refresh fails.
      }
    };
    hydrateProfile();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      if (token) {
        const resp = await updateMyProfile(token, profile);
        updateAuthProfile(resp?.data || profile);
      } else {
        updateAuthProfile(profile);
      }
      toast.success('Profile updated successfully.');
    } catch (err) {
      toast.error(err?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="agent-profile">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {mode === 'bank' ? 'Bank Details' : mode === 'support' ? 'Support' : 'My Profile'}
          </h1>
          <p className="text-muted-foreground mt-1">Manage your profile and payout settings</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild size="sm" variant={mode === 'profile' ? 'default' : 'outline'}>
            <Link to="/agent/profile">Profile</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'bank' ? 'default' : 'outline'}>
            <Link to="/agent/profile/bank">Bank Details</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'support' ? 'default' : 'outline'}>
            <Link to="/agent/profile/support">Support</Link>
          </Button>
        </div>
      </div>

      {mode === 'profile' && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>View and update your profile details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input id="full_name" name="full_name" value={profile.full_name} onChange={handleChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Agent Code</Label>
                <Input
                  id="code"
                  value={user?.id ? `AG-${String(user.id).slice(-6).toUpperCase()}` : 'AGENT'}
                  readOnly
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" name="email" value={profile.email} onChange={handleChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" name="phone" value={profile.phone} onChange={handleChange} />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
              {chip('KYC: Verified')}
              {chip('Payouts: Enabled')}
            </div>

            <div className="flex gap-3">
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveProfile} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  setProfile({
                    full_name: user?.full_name || user?.name || '',
                    email: user?.email || '',
                    phone: user?.phone || '',
                  })
                }
              >
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === 'bank' && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Bank Details</CardTitle>
            <CardDescription>Used for commission payouts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="accName">Account Holder Name</Label>
                <Input id="accName" defaultValue="Amit Patel" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc">Account Number</Label>
                <Input id="acc" defaultValue="********5678" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ifsc">IFSC</Label>
                <Input id="ifsc" defaultValue="SBIN0000123" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank">Bank Name</Label>
                <Input id="bank" defaultValue="SBI" />
              </div>
            </div>
            <div className="flex gap-3">
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => toast.success('Bank details saved (mock).')}>
                Save Bank Details
              </Button>
              <Button variant="outline" onClick={() => toast.message('No server connected in mock mode.')}>
                Verify
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === 'support' && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Support</CardTitle>
            <CardDescription>Need help? Raise a ticket (mock)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input id="subject" placeholder="e.g. Payout pending issue" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Input id="message" placeholder="Describe your issue..." />
            </div>
            <div className="flex gap-3">
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => toast.success('Support request sent (mock).')}>
                Send
              </Button>
              <Button variant="outline" onClick={() => toast.message('Support inbox is not connected in mock mode.')}>
                View Tickets
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
